import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, Check, X } from 'lucide-react';

const SearchableSelect = ({
  label,
  value,
  onChange,
  options = [],
  placeholder = 'Select an option',
  searchPlaceholder = 'Search...',
  error,
  required = false,
  disabled = false,
  className = '',
  optionLabel = 'label',
  optionValue = 'value'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Filter options based on search term
  const filteredOptions = options.filter(option => {
    const label = typeof option === 'object' ? option[optionLabel] : option;
    // Handle undefined or null labels
    if (!label) return false;
    return String(label).toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Get display value
  const getDisplayValue = () => {
    if (!value) return '';
    
    const selectedOption = options.find(option => {
      const optValue = typeof option === 'object' ? option[optionValue] : option;
      return optValue === value;
    });

    if (selectedOption) {
      return typeof selectedOption === 'object' ? selectedOption[optionLabel] : selectedOption;
    }
    
    return value;
  };

  const handleSelect = (option) => {
    const optValue = typeof option === 'object' ? option[optionValue] : option;
    onChange({ target: { value: optValue } });
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange({ target: { value: '' } });
  };

  return (
    <div className={`mb-4 ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={`
            w-full px-3 py-2.5 text-left bg-white border rounded-lg
            flex items-center justify-between
            transition-all duration-200
            ${disabled ? 'bg-gray-50 cursor-not-allowed' : 'hover:border-gray-400 hover:shadow-sm cursor-pointer'}
            ${error ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-primary-500'}
            ${isOpen ? 'ring-2 ring-primary-500 border-primary-500 shadow-sm' : ''}
          `}
        >
          <span className={`block truncate text-sm ${!value ? 'text-gray-400' : 'text-gray-900 font-medium'}`}>
            {getDisplayValue() || placeholder}
          </span>
          <div className="flex items-center gap-2">
            {value && !disabled && (
              <button
                type="button"
                onClick={handleClear}
                className="p-1 hover:bg-gray-100 rounded-md transition-colors"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            )}
            <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </div>
        </button>

        {isOpen && (
          <div className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-xl max-h-80 overflow-hidden">
            {/* Search Input */}
            <div className="p-3 border-b border-gray-200 bg-gray-50">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm bg-white"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>

            {/* Options List */}
            <div className="max-h-60 overflow-y-auto">
              {filteredOptions.length > 0 ? (
                <div className="py-1">
                  {filteredOptions.map((option, index) => {
                    const optValue = typeof option === 'object' ? option[optionValue] : option;
                    const optLabel = typeof option === 'object' ? option[optionLabel] : option;
                    const optDescription = typeof option === 'object' ? option.description : null;
                    const isSelected = optValue === value;

                    return (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleSelect(option)}
                        className={`
                          w-full px-4 py-2.5 text-left
                          flex items-center justify-between group
                          transition-all duration-150
                          ${isSelected
                            ? 'bg-primary-50 text-primary-900 hover:bg-primary-100 border-l-4 border-primary-600'
                            : 'text-gray-900 hover:bg-gray-50 border-l-4 border-transparent'}
                        `}
                      >
                        <div className="flex-1">
                          <div className={`text-sm ${isSelected ? 'font-semibold' : 'font-medium'}`}>{optLabel}</div>
                          {optDescription && (
                            <div className="text-xs text-gray-500 mt-0.5">{optDescription}</div>
                          )}
                        </div>
                        {isSelected && (
                          <Check className="w-4 h-4 text-primary-600 flex-shrink-0 ml-3" />
                        )}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="px-4 py-8 text-center text-gray-500">
                  <Search className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm font-medium">No options found</p>
                  {searchTerm && (
                    <p className="text-xs mt-1">Try a different search term</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default SearchableSelect;